import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { getStoredToken } from '../services/api';

export const SocketContext = createContext(null);
const SOCKET_URL    = process.env.REACT_APP_SOCKET_URL || '';

export function SocketProvider({ children }) {
  const socketRef      = useRef(null);
  const [socket,       setSocket]       = useState(null);
  const [isConnected,  setIsConnected]  = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);

  const connect = () => {
    const token = getStoredToken();
    if (!token) return;
    if (socketRef.current?.connected) return;

    // Cleanup any existing disconnected socket
    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    const s = io(SOCKET_URL, {
      auth:                 { token },
      transports:           ['websocket', 'polling'],
      reconnection:         true,
      reconnectionDelay:    2000,
      reconnectionDelayMax: 10000,
      reconnectionAttempts: Infinity,
      timeout:              20000,
      pingTimeout:          60000,
      pingInterval:         25000,
    });

    s.on('connect', () => {
      setIsConnected(true);
      setIsReconnecting(false);
      s.emit('join-room', {});
    });

    s.on('disconnect', (reason) => {
      setIsConnected(false);
      if (reason !== 'io server disconnect' && reason !== 'io client disconnect') {
        setIsReconnecting(true);
      }
    });

    s.on('reconnect_attempt', () => setIsReconnecting(true));
    s.on('reconnect',         () => { setIsConnected(true); setIsReconnecting(false); });
    s.on('reconnect_failed',  () => setIsReconnecting(false));
    s.on('connect_error',     () => { setIsConnected(false); setIsReconnecting(true); });

    socketRef.current = s;
    setSocket(s);
  };

  const disconnect = () => {
    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
      setSocket(null);
      setIsConnected(false);
      setIsReconnecting(false);
    }
  };

  // Mount: connect if token exists
  useEffect(() => {
    if (getStoredToken()) connect();
    const onLogin  = () => connect();
    const onLogout = () => disconnect();
    window.addEventListener('auth:login',  onLogin);
    window.addEventListener('auth:logout', onLogout);
    return () => {
      window.removeEventListener('auth:login',  onLogin);
      window.removeEventListener('auth:logout', onLogout);
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reconnect when network comes back online
  useEffect(() => {
    const onOnline = () => {
      if (getStoredToken() && !socketRef.current?.connected) {
        connect();
      }
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SocketContext.Provider value={{ socket, isConnected, isReconnecting }}>
      {children}
    </SocketContext.Provider>
  );
}

export const useSocket = () => {
  const ctx = useContext(SocketContext);
  if (!ctx) throw new Error('useSocket must be used within SocketProvider');
  return ctx;
};
